import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Send, Loader2, MessageCircle, X, Sparkles, ChevronLeft, Bot, ExternalLink } from "lucide-react";
import api from "../lib/api";

/**
 * LSSDChatbot — assistente AI generalista LSSD.
 * - Bottom-right, floating (portal on document.body)
 * - Anteprima: pill "LSSD CHAT" con dot animato
 * - Chat panel: welcome + quick actions in stile LASD → conversazione con AI
 * - Solo pagine pubbliche (nascosto su /dashboard/*)
 */

const QUICK_ACTIONS = [
  { label: "Come mi candido?",         q: "Come posso candidarmi al LSSD? Quali sono i requisiti?" },
  { label: "Struttura del Dipartimento", q: "Quali sono le divisioni e i gradi del LSSD?" },
  { label: "Verifica Stato Bando",     q: "Come posso verificare lo stato della mia candidatura?" },
  { label: "Codice Penale",            q: "Come funziona il Codice Penale di San Andreas?" },
  { label: "Contatti / Discord",       q: "Come posso contattare lo staff LSSD?" },
  { label: "Cos'è il LSSD?",           q: "Raccontami cos'è il LSSD e la sua storia." },
];

const WELCOME = {
  role: "assistant",
  content: "Benvenuto nella LSSD Chat. Sono l'Assistente AI del Los Santos County Sheriff's Department. Come posso aiutarti oggi?",
};
const EMERGENCY_NOTICE = {
  role: "assistant",
  content: "In caso di emergenza reale, contatta i servizi di emergenza del tuo paese (in Italia: 112). Questa chat è per il contesto RP del LSSD.",
};

export default function LSSDChatbot() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [screen, setScreen] = useState("welcome"); // welcome | chat
  const [messages, setMessages] = useState([WELCOME, EMERGENCY_NOTICE]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Nascondi sulle pagine autenticate (dashboard, area privata)
  const isPrivate = /^\/(dashboard|area-|admin|hr|comando|allievi|agenti)/i.test(pathname);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, screen]);

  const askQuestion = async (text) => {
    const txt = (text || input).trim();
    if (!txt || sending) return;
    setScreen("chat");
    const next = [...messages, { role: "user", content: txt }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/lssd-chat", {
        message: txt,
        history: next.slice(-6, -1),
      });
      setMessages([...next, { role: "assistant", content: data.reply || "—" }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "L'assistente non è raggiungibile in questo momento.";
      setMessages([...next, { role: "assistant", content: `⚠️ ${detail}` }]);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setScreen("welcome");
    setMessages([WELCOME, EMERGENCY_NOTICE]);
  };

  if (isPrivate) return null;

  const content = (
    <>
      {/* ============ ANTEPRIMA (pill fissa) ============ */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="lssd-chat-open"
          className="fixed bottom-6 right-6 z-[9999] group inline-flex items-center gap-2.5 rounded-full pl-4 pr-5 py-3 bg-[var(--lssd-olive-deep)] hover:bg-[var(--lssd-olive-dark)] border border-[var(--lssd-khaki)]/50 hover:border-[var(--lssd-khaki)] shadow-2xl shadow-black/40 transition-all hover:-translate-y-0.5"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--lssd-khaki)] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--lssd-khaki)]" />
          </span>
          <MessageCircle className="h-4 w-4 text-[var(--lssd-khaki)]" />
          <span className="font-display font-black text-[13px] tracking-[0.14em] text-[var(--lssd-cream)]">
            LSSD <span className="text-[var(--lssd-khaki)]">CHAT</span>
          </span>
        </button>
      )}

      {/* ============ PANEL ============ */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-[9999] w-[min(400px,calc(100vw-2rem))] h-[min(600px,calc(100vh-3rem))] rounded-2xl overflow-hidden shadow-2xl shadow-black/50 flex flex-col bg-[var(--lssd-olive-deep)] border border-[var(--lssd-khaki)]/45"
          data-testid="lssd-chat-panel"
        >
          {/* Header */}
          <div className="relative bg-[var(--lssd-olive-dark)] border-b border-[var(--lssd-khaki)]/30 px-4 py-3 flex items-center gap-2">
            <div className="absolute top-0 inset-x-4 h-[2px] bg-gradient-to-r from-transparent via-[var(--lssd-khaki)] to-transparent" />
            {screen === "chat" && (
              <button
                onClick={reset}
                data-testid="lssd-chat-back"
                className="text-[var(--lssd-khaki)]/70 hover:text-[var(--lssd-khaki)] p-1 -ml-1"
                aria-label="Torna alla schermata iniziale"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <div className="flex items-center gap-2 flex-1">
              <div className="h-9 w-9 rounded-full bg-[var(--lssd-khaki)]/18 border border-[var(--lssd-khaki)]/50 flex items-center justify-center shrink-0">
                <Bot className="h-4 w-4 text-[var(--lssd-khaki)]" />
              </div>
              <div className="leading-tight">
                <div className="font-display font-black text-[14px] text-[var(--lssd-cream)]">
                  LSSD <span className="text-[var(--lssd-khaki)]">Chat</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-[var(--lssd-khaki)]/80 flex items-center gap-1.5">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                  </span>
                  Assistente Online
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="lssd-chat-close"
              className="text-[var(--lssd-cream)]/50 hover:text-rose-300 p-1"
              aria-label="Chiudi chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin">
            {messages.map((m, i) => (
              <ChatBubble key={`${i}-${m.role}`} role={m.role} content={m.content} />
            ))}

            {/* Quick actions (mostrate solo su welcome) */}
            {screen === "welcome" && !sending && (
              <div className="pt-2" data-testid="lssd-chat-quick-actions">
                <div className="text-[10px] uppercase tracking-[0.22em] font-mono font-bold text-[var(--lssd-khaki)]/70 mb-2.5 text-center">
                  Scegli una delle opzioni
                </div>
                <div className="grid gap-2">
                  {QUICK_ACTIONS.map((q) => (
                    <button
                      key={q.label}
                      onClick={() => askQuestion(q.q)}
                      data-testid={`lssd-chat-qa-${q.label.replace(/\s/g, "-").toLowerCase()}`}
                      className="text-left rounded-full px-4 py-2 border border-[var(--lssd-khaki)]/40 text-[13px] text-[var(--lssd-cream)] hover:bg-[var(--lssd-khaki)]/12 hover:border-[var(--lssd-khaki)] transition-colors flex items-center justify-between group"
                    >
                      <span>→ {q.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {sending && (
              <div className="flex items-center gap-2 text-xs text-[var(--lssd-khaki)]/85 pl-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sto pensando…
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-[var(--lssd-khaki)]/25 p-3 bg-[var(--lssd-olive-dark)]/60">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); askQuestion(); } }}
                placeholder="Scrivi la tua domanda…"
                className="flex-1 bg-[var(--lssd-olive)] border border-[var(--lssd-khaki)]/30 focus:border-[var(--lssd-khaki)] rounded-full px-4 py-2 text-[13px] text-[var(--lssd-cream)] placeholder:text-[var(--lssd-cream)]/40 focus:outline-none"
                disabled={sending}
                maxLength={1000}
                data-testid="lssd-chat-input"
              />
              <button
                onClick={() => askQuestion()}
                disabled={sending || !input.trim()}
                data-testid="lssd-chat-send"
                className="rounded-full bg-[var(--lssd-khaki)] hover:bg-[var(--lssd-khaki-light)] text-[var(--lssd-olive-deep)] w-10 h-10 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Invia"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between text-[9.5px] uppercase tracking-[0.2em] font-mono text-[var(--lssd-khaki)]/50">
              <span className="inline-flex items-center gap-1">
                <Sparkles className="h-2.5 w-2.5" /> Powered by AI
              </span>
              <a
                href="https://discord.gg/JQE6UTAuP9"
                target="_blank"
                rel="noreferrer"
                className="hover:text-[var(--lssd-khaki)] inline-flex items-center gap-1"
              >
                Discord <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(content, document.body);
}

function ChatBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[var(--lssd-khaki)]/22 text-[var(--lssd-cream)] border border-[var(--lssd-khaki)]/40"
            : "bg-[var(--lssd-olive-dark)] text-[var(--lssd-cream)]/90 border border-[var(--lssd-khaki)]/18"
        }`}
      >
        {!isUser && (
          <div className="text-[9px] uppercase tracking-[0.22em] font-mono font-bold text-[var(--lssd-khaki)] mb-1 inline-flex items-center gap-1">
            <Sparkles className="h-2.5 w-2.5" /> LSSD AI
          </div>
        )}
        <div>{content}</div>
      </div>
    </div>
  );
}
